import { useRef, useState, useEffect } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { cn, formatCurrency } from '@/lib/utils'
import type { Devis, SignatureData } from '@/types/devis'
import {
  PenTool,
  Type,
  Check,
  Eraser,
} from 'lucide-react'

interface SignatureCanvasProps {
  canvasRef: React.RefObject<HTMLCanvasElement>
  width?: number
  height?: number
  className?: string
}

export function SignatureCanvas({
  canvasRef,
  width = 400,
  height = 150,
  className,
}: SignatureCanvasProps) {
  const [isDrawing, setIsDrawing] = useState(false)
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.strokeStyle = '#3D2914' // brown-900
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctxRef.current = ctx

    // Clear canvas with white background
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, width, height)
  }, [canvasRef, width, height])

  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    if ('touches' in e) {
      const touch = e.touches[0]
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      }
    }

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    const { x, y } = getCoordinates(e)
    if (!ctxRef.current) return

    ctxRef.current.beginPath()
    ctxRef.current.moveTo(x, y)
    setIsDrawing(true)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    if (!isDrawing || !ctxRef.current) return

    const { x, y } = getCoordinates(e)
    ctxRef.current.lineTo(x, y)
    ctxRef.current.stroke()
  }

  const stopDrawing = () => {
    if (!ctxRef.current) return
    ctxRef.current.closePath()
    setIsDrawing(false)
  }

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={cn(
        'w-full bg-white rounded cursor-crosshair touch-none',
        className
      )}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
      onTouchStart={startDrawing}
      onTouchMove={draw}
      onTouchEnd={stopDrawing}
    />
  )
}

export function clearCanvas(canvasRef: React.RefObject<HTMLCanvasElement>) {
  const canvas = canvasRef.current
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
}

export function isCanvasEmpty(canvasRef: React.RefObject<HTMLCanvasElement>): boolean {
  const canvas = canvasRef.current
  if (!canvas) return true

  const ctx = canvas.getContext('2d')
  if (!ctx) return true

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  // Check if all pixels are white
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) {
      return false
    }
  }

  return true
}

interface SignatureModalProps {
  devis: Devis
  isOpen: boolean
  onClose: () => void
  onSign: (signature: SignatureData) => void
  isLoading?: boolean
}

export function SignatureModal({
  devis,
  isOpen,
  onClose,
  onSign,
  isLoading = false,
}: SignatureModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [accepted, setAccepted] = useState(false)
  const [signatureType, setSignatureType] = useState<'draw' | 'type'>('draw')
  const [typedName, setTypedName] = useState('')
  const [error, setError] = useState('')

  const handleClear = () => {
    clearCanvas(canvasRef)
    setError('')
  }

  const handleSign = async () => {
    setError('')

    if (!accepted) {
      setError('Vous devez accepter les conditions pour signer')
      return
    }

    let signatureData: string

    if (signatureType === 'draw') {
      if (isCanvasEmpty(canvasRef)) {
        setError('Veuillez dessiner votre signature')
        return
      }
      signatureData = canvasRef.current?.toDataURL('image/png') || ''
    } else {
      if (!typedName.trim()) {
        setError('Veuillez entrer votre nom')
        return
      }
      signatureData = `[Signature tapee] ${typedName.trim()}`
    }

    // Get client IP (this would typically come from server-side)
    let ip = 'unknown'
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      ip = data.ip
    } catch {
      // Fallback if IP detection fails
    }

    onSign({
      signature: signatureData,
      accepted_conditions: accepted,
      timestamp: new Date().toISOString(),
      ip,
      typed_name: signatureType === 'type' ? typedName.trim() : undefined,
    })
  }

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setAccepted(false)
      setSignatureType('draw')
      setTypedName('')
      setError('')
    }
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Signer le devis" size="lg">
      <div className="space-y-6">
        {/* Summary */}
        <div className="bg-beige-50 p-4 rounded-lg">
          <p className="font-medium text-brown-900">Devis {devis.numero}</p>
          <p className="text-2xl font-bold text-mint-600 mt-1">
            {formatCurrency(devis.montant_ttc)}
          </p>
        </div>

        {/* Conditions acceptance */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => {
              setAccepted(e.target.checked)
              setError('')
            }}
            className="mt-1 w-4 h-4 rounded border-beige-300 text-mint-600 focus:ring-mint-500"
          />
          <span className="text-sm text-brown-700">
            J'accepte les conditions generales de vente et les termes de ce devis.
            Je m'engage a regler la facture correspondante selon les conditions indiquees.
          </span>
        </label>

        {/* Signature type selection */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setSignatureType('draw')}
            className={cn(
              'flex-1 p-3 rounded-lg border-2 transition-colors',
              signatureType === 'draw'
                ? 'border-mint-500 bg-mint-50'
                : 'border-beige-200 hover:border-beige-300'
            )}
          >
            <PenTool className="w-5 h-5 mx-auto mb-1 text-brown-700" />
            <span className="text-sm font-medium text-brown-700">Dessiner</span>
          </button>
          <button
            type="button"
            onClick={() => setSignatureType('type')}
            className={cn(
              'flex-1 p-3 rounded-lg border-2 transition-colors',
              signatureType === 'type'
                ? 'border-mint-500 bg-mint-50'
                : 'border-beige-200 hover:border-beige-300'
            )}
          >
            <Type className="w-5 h-5 mx-auto mb-1 text-brown-700" />
            <span className="text-sm font-medium text-brown-700">Taper</span>
          </button>
        </div>

        {/* Signature area */}
        {signatureType === 'draw' ? (
          <div className="space-y-2">
            <div className="border-2 border-dashed border-beige-300 rounded-lg p-2">
              <SignatureCanvas
                canvasRef={canvasRef}
                width={400}
                height={150}
              />
            </div>
            <div className="flex justify-between items-center">
              <p className="text-xs text-brown-500">
                Dessinez votre signature dans le cadre ci-dessus
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                leftIcon={<Eraser className="w-4 h-4" />}
              >
                Effacer
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Input
              value={typedName}
              onChange={(e) => {
                setTypedName(e.target.value)
                setError('')
              }}
              placeholder="Tapez votre nom complet"
              className="text-center text-xl italic"
            />
            <p className="text-xs text-brown-500 text-center">
              En tapant votre nom, vous confirmez votre accord
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <p className="text-sm text-red-600 text-center">{error}</p>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSign}
            disabled={!accepted || (signatureType === 'type' && !typedName.trim())}
            isLoading={isLoading}
            className="flex-1"
            leftIcon={<Check className="w-4 h-4" />}
          >
            Signer et accepter
          </Button>
        </div>
      </div>
    </Modal>
  )
}

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (message: string) => void
  isLoading?: boolean
}

export function FeedbackModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}: FeedbackModalProps) {
  const [message, setMessage] = useState('')

  const handleSubmit = () => {
    if (!message.trim()) return
    onSubmit(message.trim())
  }

  useEffect(() => {
    if (isOpen) {
      setMessage('')
    }
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Demander des modifications">
      <div className="space-y-4">
        <p className="text-sm text-brown-600">
          Expliquez les modifications souhaitees ou posez vos questions.
          L'equipe LesSablesWeb reviendra vers vous rapidement.
        </p>

        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">
            Votre message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Decrivez les modifications souhaitees..."
            rows={4}
            className="block w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
          />
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!message.trim()}
            isLoading={isLoading}
            className="flex-1"
          >
            Envoyer
          </Button>
        </div>
      </div>
    </Modal>
  )
}
